import {
  OFFLINE_DB_NAME,
  OFFLINE_DB_VERSION,
  PENDING_SYNC_STORE,
  CACHE_STORE,
  FAILED_SYNC_STORE,
} from './constants';

// This should be updated and ensure that the properties are correctin indexDB
export interface PendingRequest {
  id: string;
  status: 'pending' | 'failed';
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  endpoint: string;
  body?: unknown;
  companyId?: string | null;
  timestamp: number;
  failedAt?: number;
  errorMessage?: string;
  localId?: string;
}

export type FailedRequest = PendingRequest;
export interface CachedData {
  key: string; // endpoint + queryParams
  data: unknown;
  timestamp: number;
}

let db: IDBDatabase | null = null;
let dbPromise: Promise<IDBDatabase> | null = null;

/**
 * Open the offline IndexedDB
 */
export function openOfflineDatabase(): Promise<IDBDatabase> {
  if (db) return Promise.resolve(db);
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(OFFLINE_DB_NAME, OFFLINE_DB_VERSION);

    request.onerror = () => {
      dbPromise = null;
      reject(new Error('Failed to open offline database'));
    };

    request.onsuccess = () => {
      db = request.result;
      dbPromise = null;
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const database = (event.target as IDBOpenDBRequest).result;

      if (!database.objectStoreNames.contains(PENDING_SYNC_STORE)) {
        database.createObjectStore(PENDING_SYNC_STORE, { keyPath: 'id' });
      }

      if (!database.objectStoreNames.contains(CACHE_STORE)) {
        database.createObjectStore(CACHE_STORE, { keyPath: 'key' });
      }

      if (!database.objectStoreNames.contains(FAILED_SYNC_STORE)) {
        database.createObjectStore(FAILED_SYNC_STORE, { keyPath: 'id' });
      }
    };

    request.onblocked = () => {
      console.warn('Database upgrade blocked. Please close other tabs.');
    };
  });

  return dbPromise;
}

/**
 * Save a request for later synchronization
 */
export async function savePendingRequest(
  method: PendingRequest['method'],
  endpoint: string,
  body?: any,
  companyId?: string | null,
): Promise<string> {
  const database = await openOfflineDatabase();
  const id = `local_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  const pendingRequest: PendingRequest = {
    id,
    status: 'pending',
    method,
    endpoint,
    body,
    companyId,
    timestamp: Date.now(),
  };

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([PENDING_SYNC_STORE], 'readwrite');
    const store = transaction.objectStore(PENDING_SYNC_STORE);
    const request = store.put(pendingRequest);

    request.onsuccess = () => {
      // Dispatch event to notify listeners that a pending request was added
      window.dispatchEvent(new CustomEvent('sync-request-pending'));
      resolve(id);
    };
    request.onerror = () => reject(new Error('Failed to save pending request'));
  });
}

/**
 * Get all pending requests sorted by timestamp
 */
export async function getPendingRequests(): Promise<PendingRequest[]> {
  const database = await openOfflineDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([PENDING_SYNC_STORE], 'readonly');
    const store = transaction.objectStore(PENDING_SYNC_STORE);
    const request = store.getAll();

    request.onsuccess = () => {
      const results = (request.result as PendingRequest[]) || [];
      results.sort((a, b) => a.timestamp - b.timestamp);
      resolve(results);
    };
    request.onerror = () => reject(new Error('Failed to get pending requests'));
  });
}

/**
 * Get all pending requests sorted by timestamp
 */
export async function getFailedRequests(): Promise<PendingRequest[]> {
  const database = await openOfflineDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([FAILED_SYNC_STORE], 'readonly');
    const store = transaction.objectStore(FAILED_SYNC_STORE);
    const request = store.getAll();

    request.onsuccess = () => {
      const results = (request.result as FailedRequest[]) || [];
      results.sort((a, b) => a.timestamp - b.timestamp);
      resolve(results);
    };
    request.onerror = () => reject(new Error('Failed to get failed requests'));
  });
}

/**
 * Get a single failed request by id
 */
export async function getFailedRequest(
  id: string,
): Promise<PendingRequest | null> {
  const database = await openOfflineDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([FAILED_SYNC_STORE], 'readonly');
    const store = transaction.objectStore(FAILED_SYNC_STORE);
    const request = store.get(id);

    request.onsuccess = () => {
      const result = request.result as PendingRequest | undefined;
      resolve(result ?? null);
    };
    request.onerror = () =>
      reject(new Error('Failed to get failed request by id'));
  });
}

/**
 * Update a pending request's body
 */
export async function updatePendingRequest(
  id: string,
  body: unknown,
): Promise<void> {
  const database = await openOfflineDatabase();
  return new Promise<void>((resolve, reject) => {
    const transaction = database.transaction([PENDING_SYNC_STORE], 'readwrite');
    const store = transaction.objectStore(PENDING_SYNC_STORE);

    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const existing = getRequest.result as PendingRequest;
      if (!existing)
        return reject(new Error('Request not found in pending store'));

      const updatedRequest = {
        ...existing,
        body,
        timestamp: Date.now(), // Update timestamp
      };
      const putRequest = store.put(updatedRequest);

      putRequest.onsuccess = () => {
        // Dispatch event to notify listeners that a pending request was updated
        window.dispatchEvent(new CustomEvent('sync-request-pending'));
        resolve();
      };
      putRequest.onerror = () =>
        reject(new Error('Failed to update pending request'));
    };

    getRequest.onerror = () =>
      reject(new Error('Failed to access request in pending store'));
  });
}

/**
 * Delete a pending request
 */
export async function deletePendingRequest(id: string): Promise<void> {
  const database = await openOfflineDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([PENDING_SYNC_STORE], 'readwrite');
    const store = transaction.objectStore(PENDING_SYNC_STORE);
    const request = store.delete(id);

    request.onsuccess = () => {
      // Dispatch event to notify listeners that a pending request was deleted
      window.dispatchEvent(new CustomEvent('sync-request-pending'));
      resolve();
    };
    request.onerror = () =>
      reject(new Error('Failed to delete pending request'));
  });
}

/**
 * Move a request to failed store
 */
export const markRequestFailedInOtherDB = async (
  id: string,
  errorMessage?: string,
): Promise<void> => {
  const database = await openOfflineDatabase();

  return new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(
      [PENDING_SYNC_STORE, FAILED_SYNC_STORE],
      'readwrite',
    );

    const pendingStore = transaction.objectStore(PENDING_SYNC_STORE);
    const failedStore = transaction.objectStore(FAILED_SYNC_STORE);

    const getRequest = pendingStore.get(id);

    getRequest.onsuccess = () => {
      const existing = getRequest.result as PendingRequest;
      if (!existing)
        return reject(new Error('Request not found in pending store'));

      // Add timestamp for failed and error message
      const failedRequest = {
        ...existing,
        failedAt: Date.now(),
        status: 'failed',
        errorMessage: errorMessage || existing.errorMessage,
      };
      const putFailed = failedStore.put(failedRequest);

      putFailed.onsuccess = () => {
        const deleteRequest = pendingStore.delete(id);
        deleteRequest.onsuccess = () => resolve();
        deleteRequest.onerror = () =>
          reject(new Error('Failed to remove request from pending store'));
      };

      putFailed.onerror = () =>
        reject(new Error('Failed to save request in failed store'));
    };

    getRequest.onerror = () =>
      reject(new Error('Failed to access request in pending store'));
  });
};

/**
 * Cache GET request data
 */
export async function cacheData(key: string, data: unknown): Promise<void> {
  const database = await openOfflineDatabase();
  const cached: CachedData = { key, data, timestamp: Date.now() };

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([CACHE_STORE], 'readwrite');
    const store = transaction.objectStore(CACHE_STORE);
    const request = store.put(cached);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error('Failed to cache data'));
  });
}

/**
 * Get cached data
 */
export async function getCachedData(key: string): Promise<unknown> {
  const database = await openOfflineDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([CACHE_STORE], 'readonly');
    const store = transaction.objectStore(CACHE_STORE);
    const request = store.get(key);

    request.onsuccess = () => {
      const result = request.result as CachedData | undefined;
      resolve(result ? result.data : null);
    };
    request.onerror = () => reject(new Error('Failed to get cached data'));
  });
}

/**
 * Delete cached offline responses for Kanban board endpoints.
 * Helps prevent cross-user data leaks when switching authenticated users.
 */
export async function clearKanbanOfflineCache(): Promise<void> {
  const database = await openOfflineDatabase();

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([CACHE_STORE], 'readwrite');
    const store = transaction.objectStore(CACHE_STORE);

    const request = store.getAll();
    request.onsuccess = () => {
      const items = (request.result ?? []) as CachedData[];
      const keysToDelete = items
        .map((i) => i.key)
        .filter((key) => typeof key === 'string' && key.startsWith('/boards'));

      keysToDelete.forEach((key) => {
        store.delete(key);
      });
    };
    request.onerror = () =>
      reject(new Error('Failed to read offline cache for Kanban'));

    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(new Error('Failed to clear offline Kanban cache'));
  });
}

/**
 * Generate cache key
 */
export function generateCacheKey(
  endpoint: string,
  queryParams?: Record<string, string>,
  companyId?: string | null,
): string {
  const params = new URLSearchParams();
  if (companyId) params.append('companyId', companyId);
  if (queryParams) {
    Object.entries(queryParams).forEach(([k, v]) => params.append(k, v));
  }
  return `${endpoint}?${params.toString()}`;
}

/**
 * Delete a failed request
 */
export async function deleteFailedRequest(id: string): Promise<void> {
  const database = await openOfflineDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([FAILED_SYNC_STORE], 'readwrite');
    const store = transaction.objectStore(FAILED_SYNC_STORE);
    const request = store.delete(id);

    request.onsuccess = () => {
      // Dispatch event to notify listeners that a failed request was deleted
      window.dispatchEvent(new CustomEvent('sync-request-failed'));
      resolve();
    };
    request.onerror = () =>
      reject(new Error('Failed to delete failed request'));
  });
}

/**
 * Update a failed request (move it back to pending)
 */
export async function retryFailedRequest(id: string): Promise<void> {
  const database = await openOfflineDatabase();

  return new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(
      [FAILED_SYNC_STORE, PENDING_SYNC_STORE],
      'readwrite',
    );

    const failedStore = transaction.objectStore(FAILED_SYNC_STORE);
    const pendingStore = transaction.objectStore(PENDING_SYNC_STORE);

    const getRequest = failedStore.get(id);

    getRequest.onsuccess = () => {
      const existing = getRequest.result as PendingRequest;
      if (!existing)
        return reject(new Error('Request not found in failed store'));

      // Move back to pending with updated timestamp
      const pendingRequest = {
        ...existing,
        status: 'pending' as const,
        timestamp: Date.now(),
      };
      const putPending = pendingStore.put(pendingRequest);

      putPending.onsuccess = () => {
        const deleteRequest = failedStore.delete(id);
        deleteRequest.onsuccess = () => resolve();
        deleteRequest.onerror = () =>
          reject(new Error('Failed to remove request from failed store'));
      };

      putPending.onerror = () =>
        reject(new Error('Failed to save request in pending store'));
    };

    getRequest.onerror = () =>
      reject(new Error('Failed to access request in failed store'));
  });
}

/**
 * Move a failed request back to the pending store with optional updated body.
 *
 * This is a convenience helper when you already know which failed request
 * you want to retry and you may want to override its body before re‑queuing.
 *
 * - If `body` is provided, it will replace the existing body.
 * - The request's status is set to `pending` and timestamp is refreshed.
 * - `failedAt` and `errorMessage` are cleared when moving back to pending.
 */
export async function moveFailedRequestToPending(
  id: string,
  body?: unknown,
): Promise<void> {
  const database = await openOfflineDatabase();

  return new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(
      [FAILED_SYNC_STORE, PENDING_SYNC_STORE],
      'readwrite',
    );

    const failedStore = transaction.objectStore(FAILED_SYNC_STORE);
    const pendingStore = transaction.objectStore(PENDING_SYNC_STORE);

    const getRequest = failedStore.get(id);

    getRequest.onsuccess = () => {
      const existing = getRequest.result as PendingRequest;
      if (!existing) {
        return reject(new Error('Request not found in failed store'));
      }

      const pendingRequest: PendingRequest = {
        ...existing,
        status: 'pending',
        timestamp: Date.now(),
        failedAt: undefined,
        errorMessage: undefined,
        ...(body !== undefined ? { body } : {}),
      };

      const putPending = pendingStore.put(pendingRequest);

      putPending.onsuccess = () => {
        const deleteRequest = failedStore.delete(id);
        deleteRequest.onsuccess = () => resolve();
        deleteRequest.onerror = () =>
          reject(new Error('Failed to remove request from failed store'));
      };

      putPending.onerror = () =>
        reject(new Error('Failed to save request in pending store'));
    };

    getRequest.onerror = () =>
      reject(new Error('Failed to access request in failed store'));
  });
}

/**
 * Update a failed request's body and optionally errorMessage
 */
export async function updateFailedRequest(
  id: string,
  body: unknown,
  errorMessage?: string,
): Promise<void> {
  const database = await openOfflineDatabase();

  return new Promise<void>((resolve, reject) => {
    const transaction = database.transaction([FAILED_SYNC_STORE], 'readwrite');
    const store = transaction.objectStore(FAILED_SYNC_STORE);

    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const existing = getRequest.result as PendingRequest;
      if (!existing)
        return reject(new Error('Request not found in failed store'));

      const updatedRequest: PendingRequest = {
        ...existing,
        body,
      };

      // Update top-level errorMessage if provided
      if (errorMessage !== undefined) {
        updatedRequest.errorMessage = errorMessage;
      }

      const putRequest = store.put(updatedRequest);

      putRequest.onsuccess = () => {
        // Dispatch event to notify listeners that a failed request was updated
        window.dispatchEvent(new CustomEvent('sync-request-failed'));
        resolve();
      };
      putRequest.onerror = () =>
        reject(new Error('Failed to update failed request'));
    };

    getRequest.onerror = () =>
      reject(new Error('Failed to access request in failed store'));
  });
}

/**
 * Update only the errorMessage field of a failed request
 */
export async function updateFailedRequestErrorMessage(
  id: string,
  errorMessage: string,
): Promise<void> {
  const database = await openOfflineDatabase();

  return new Promise<void>((resolve, reject) => {
    const transaction = database.transaction([FAILED_SYNC_STORE], 'readwrite');
    const store = transaction.objectStore(FAILED_SYNC_STORE);

    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const existing = getRequest.result as PendingRequest | undefined;
      // If the failed request no longer exists, treat this as a no-op
      if (!existing) {
        resolve();
        return;
      }

      const updatedRequest: PendingRequest = {
        ...existing,
        errorMessage,
        failedAt: Date.now(),
      };

      const putRequest = store.put(updatedRequest);

      putRequest.onsuccess = () => {
        // Dispatch event to notify listeners that a failed request was updated
        window.dispatchEvent(new CustomEvent('sync-request-failed'));
        resolve();
      };
      putRequest.onerror = () =>
        reject(new Error('Failed to update failed request error message'));
    };

    getRequest.onerror = () =>
      reject(new Error('Failed to access request in failed store'));
  });
}
